import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { LastLocationProvider } from 'react-router-last-location';
import { withRouter } from 'react-router';
import { renderRoutes } from 'react-router-config';
import queryString from 'query-string';

import isUserAgentMobileApp, {
  shouldShowNativeAppBanner,
  isUserAgentIphoneApp,
  shouldShowPartnerRedirectionBanner,
} from 'common/util/isUserAgentMobileApp';
import {
  USER_ROLE_ADMIN,
  hasRoleAnonymous,
  hasRoleRenter,
} from 'common/userRoles';
import {
  shouldRenterReviewTour,
} from 'common/tourHelpers';
import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_ROUTE_CHANGED,
  SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
} from 'client/helpers/sendDataToMobileApp';

import { SR_API_ACTION_GET_APP_COMMIT_HASH } from 'api/actionTypes';

import ListingActions from 'client/actions/Listing';

import branch from 'client/core/branch';
import SrFrontChat from 'client/components/SrFrontChat';
import SrMobileAppEventListener from 'client/components/SrMobileAppEventListener';
import SrUpdateMoveInDateModal from 'client/components/SrUpdateMoveInDateModal';
import SrAppStoreReviewDialog from 'client/components/SrAppStoreReviewDialog';
import SrListingSearchWidget from 'client/components/SrListingSearchWidget';
import SrTourFeedback from 'client/components/SrTourFeedback/SrTourFeedback';
import SrAskWantstoApplyModal from 'client/components/SrAskWantstoApplyModal/SrAskWantsToApplyModal';
import SrEnablePushNotificationsModal from 'client/components/SrEnablePushNotificationsModal';
import SrTourListingFailedModalProvider from 'client/components/SrTourListingFailedModal/SrTourListingFailedModalProvider';

// Interval for checking the app commit hash vs the server commit hash to reload the page when a new version is available.
const CHECK_APP_VERSION_INTERVAL_MS = 1800000; // 30 minutes.

@withRouter
@branch({
  currentUser: ['currentUser'],
  tours: ['tours'],
  askWantsToApplyToListing: ['askWantsToApplyToListing'],
  userAgent: ['analytics', 'userAgent'],
  updateMoveInDateModalOpen: ['updateMoveInDate', 'modalOpen'],
  forceMoveInDateUpdate: ['updateMoveInDate', 'forceMoveInDateUpdate'],
  mobileAppAskForReview: ['mobileApp', 'askForReview'],
  shouldRequestRenterTourFeedback: ['shouldRequestRenterTourFeedback'],
  askPushNotifications: ['mobileApp', 'askPushNotifications'],
})
class SrApp extends Component {
  static propTypes = {
    apiClient: PropTypes.object,
    userAgent: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    routes: PropTypes.array.isRequired,
    updateMoveInDateModalOpen: PropTypes.bool.isRequired,
    forceMoveInDateUpdate: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    mobileAppAskForReview: PropTypes.bool.isRequired,
    askWantsToApplyToListing: PropTypes.object,
    shouldRequestRenterTourFeedback: PropTypes.bool.isRequired,
  };

  constructor(props) {
    super(props);
    this._checkAppVersionInterval = null;
    this._checkAppVersionLastRunTimestamp = Date.now();
    this.historyUnlisten = null;
  }

  componentDidMount() {
    global.document.addEventListener('wheel', () => {
      if (global.document.activeElement.type === 'number') {
        global.document.activeElement.blur();
      }
    });
    if (!this._checkAppVersionInterval) {
      this._checkAppVersionInterval = global.setInterval(() => {
        // Avoid doing unnecesary API calls if the app is not focused (not visible).
        if (global.document && global.document.hidden) {
          return;
        }
        const now = Date.now();
        if (now - this._checkAppVersionLastRunTimestamp > CHECK_APP_VERSION_INTERVAL_MS) {
          this._checkAppVersionLastRunTimestamp = now;
          // The API client automatically compares the commit hash returned by the server in res.header['x-sr-app-commit-hash'],
          // and refresh the browser if the commit hash the app was loaded from does not match the server's commit hash.
          this.props.apiClient.post(SR_API_ACTION_GET_APP_COMMIT_HASH);
        }
      }, 1000);
    }

    const { currentUser, history, userAgent } = this.props;
    const isMobileApp = isUserAgentMobileApp(userAgent);

    if (isMobileApp) {
      if (!hasRoleAnonymous(currentUser.roles)) {
        sendDataToMobileApp({
          actionType: SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
          currentUser: currentUser,
        });
      }

      this.historyUnlisten = history.listen((location) => {
        sendDataToMobileApp({
          actionType: SR_MOBILE_APP_ACTION_TYPE_ROUTE_CHANGED,
          routePath: location.pathname,
          routeQuery: queryString.parse(location.search || ''),
          currentUser: currentUser,
        });
      });
    }
  }

  componentWillUnmount() {
    global.clearInterval(this._checkAppVersionInterval);
    this._checkAppVersionInterval = null;
    if (this.historyUnlisten) {
      this.historyUnlisten();
    }
  }

  _onWantsToApply = () => {
    const { history, dispatch, askWantsToApplyToListing } = this.props;
    history.push(`/listing/${askWantsToApplyToListing.id}?scrollTo=yourQualifications`);
    dispatch(ListingActions.askWantsToApply, null);
  }

  _onCloseWantsToApply = () => {
    this.props.dispatch(ListingActions.askWantsToApply, null);
  }

  render() {
    const {
      currentUser,
      tours,
      userAgent,
      routes,
      location,
      updateMoveInDateModalOpen,
      forceMoveInDateUpdate,
      dispatch,
      mobileAppAskForReview,
      askWantsToApplyToListing,
      shouldRequestRenterTourFeedback,
      askPushNotifications,
    } = this.props;

    const isMobileApp = isUserAgentMobileApp(userAgent);

    const showNativeAppBanner = shouldShowNativeAppBanner({
      userAgent,
      location,
      currentUser,
    });

    const showPartnerRedirectionBanner = shouldShowPartnerRedirectionBanner({
      location,
      currentUser,
    });

    const showAdminListingSearchWidget = currentUser.roles.includes(USER_ROLE_ADMIN);

    const reviewTourId = shouldRenterReviewTour({
      currentUser,
      tours,
      location,
      askWantsToApplyToListing,
    });

    return (
      <div
        className={classnames(
          'app-container',
          {
            webview: isMobileApp,
            nativeAppBannerActive: showNativeAppBanner,
            ios: isUserAgentIphoneApp(userAgent),
            partnerRedirectionBannerActive: showPartnerRedirectionBanner,
          },
        )}
      >
        <LastLocationProvider>
          {renderRoutes(routes)}
        </LastLocationProvider>
        <SrFrontChat />
        <SrMobileAppEventListener />
        <SrTourListingFailedModalProvider />
        {hasRoleRenter(currentUser.roles) && (
          <SrUpdateMoveInDateModal
            updateMoveInDateModalOpen={updateMoveInDateModalOpen}
            forceMoveInDateUpdate={forceMoveInDateUpdate}
            dispatch={dispatch}
          />
        )}
        {mobileAppAskForReview && (
          <SrAppStoreReviewDialog currentUser={currentUser} dispatch={dispatch} />
        )}
        {reviewTourId && shouldRequestRenterTourFeedback && (
          <SrTourFeedback tours={tours} tourId={reviewTourId} isMobileApp={isMobileApp} />
        )}
        {askWantsToApplyToListing && (
          <SrAskWantstoApplyModal
            listing={askWantsToApplyToListing}
            onWantsToApply={this._onWantsToApply}
            onClose={this._onCloseWantsToApply}
          />
        )}
        {showAdminListingSearchWidget && (
          <SrListingSearchWidget
            dispatch={dispatch}
          />
        )}
        {askPushNotifications && (
          <SrEnablePushNotificationsModal
            onClose={() => {}}
          />
        )}
      </div>
    );
  }
}

SrApp.displayName = 'SrApp';

export default SrApp;
